import { useState, useEffect } from "react";
import { CloseIcon, ChevronDownIcon } from "../../icons";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import SimpleDate from "./SimpleDate";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import type { Message } from "../../api/messages";

interface MessageHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  requestId?: string;
}

export default function MessageHistorySidebar({
  isOpen,
  onClose,
  requestId,
}: MessageHistorySidebarProps) {
  const { callApi } = useAuthenticatedApi();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fetch all messages and let the sidebar scroll naturally
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(
    new Set(),
  );

  const fetchMessages = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Cap to a safe page size to avoid heavy payloads
      const params: Record<string, string> = {
        page: "1",
        limit: "200",
      };

      const response = await callApi({
        method: "GET",
        url: "/management/messages",
        params,
      });

      // Filter by requestId if provided
      let filteredMessages = response.data.data;
      if (requestId) {
        filteredMessages = filteredMessages.filter(
          (msg: Message & { requestId?: string }) =>
            msg.requestId === requestId,
        );
      }

      setMessages(filteredMessages);
    } catch (err) {
      const error = err as Error;
      setError(error.message || "Failed to load message history");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMessages();
    }
  }, [isOpen, requestId]);

  const toggleExpanded = (messageId: string) => {
    setExpandedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // No pagination controls; natural scrolling in the sidebar

  return (
    <>
      {/* Backdrop: click outside sidebar (including header/app nav) to close */}
      {isOpen && (
        <div
          aria-hidden
          className="fixed inset-0 z-30 top-0 left-0 right-0 bottom-0"
          onClick={onClose}
        />
      )}
      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] 
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto z-40
          transition-transform duration-300 ease-in-out transform
          ${isOpen ? "translate-x-0" : "translate-x-full"}
      `}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-black dark:text-white">
            Message History {requestId && "(This Request)"}
          </h3>
          <button
            className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500 dark:text-red-400">Error: {error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500 dark:text-gray-400">
              No messages found
              {requestId ? " for this request" : ""}
            </p>
          </div>
        ) : (
          <>
            {/* Messages List */}
            <div className="space-y-3">
              {messages.map((message) => (
                <div
                  key={message.messageId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50"
                >
                  {/* Message Header */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {message.type}
                        </span>
                        <button
                          onClick={() => toggleExpanded(message.messageId)}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-transform duration-200 ${
                              expandedMessages.has(message.messageId)
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <SimpleDate date={message.createdAt} />
                      </div>
                    </div>
                  </div>

                  {/* Subject */}
                  {message.subject && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Subject: {message.subject}
                      </span>
                    </div>
                  )}

                  {/* Message Content Preview */}
                  <div className="text-sm text-gray-800 dark:text-gray-200">
                    {expandedMessages.has(message.messageId) ? (
                      <div className="whitespace-pre-wrap">
                        {message.content}
                      </div>
                    ) : (
                      <div className="truncate">
                        {message.content.length > 100
                          ? `${message.content.substring(0, 100)}...`
                          : message.content}
                      </div>
                    )}
                  </div>

                  {/* Expanded Details */}
                  {expandedMessages.has(message.messageId) && (
                    <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600 space-y-2 text-xs">
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          From:
                        </span>{" "}
                        <span className="text-gray-800 dark:text-gray-200">
                          {message.sender.name}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          To:
                        </span>{" "}
                        <span className="text-gray-800 dark:text-gray-200">
                          Guest ({message.receiver.email || "No email"})
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          Message ID:
                        </span>{" "}
                        <span className="text-gray-800 dark:text-gray-200 font-mono text-xs">
                          {message.messageId}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* No pagination; list scrolls within the sidebar */}
          </>
        )}
      </div>
    </>
  );
}
